import type { AuthUser } from "~/hooks/useAuth";

export const SUBSCRIBE_PATH = "/subscribe";
export const ONBOARDING_PATH = "/onboarding";
export const DASHBOARD_PATH = "/dashboard";
export const SUBSCRIPTION_PATH = "/subscription";

type PreferenceBag = Record<string, unknown> | null | undefined;

export function hasPaidAccess(user: AuthUser | null | undefined) {
  if (!user) return false;
  return user.subscriptionStatus === "active";
}

export function hasEverSubscribed(user: AuthUser | null | undefined) {
  if (!user) return false;
  return Boolean(user.subscriptionEventAt) || hasPaidAccess(user);
}

export function isInitialOnboardingComplete(user: AuthUser | null | undefined) {
  const preferences = user?.preferences as PreferenceBag;
  return preferences?.initialOnboardingCompleted === true;
}

export function canEnterApp(
  user: AuthUser | null | undefined,
  options?: { hasSharedWorkspace?: boolean },
) {
  if (!user) return false;
  return (
    hasPaidAccess(user) ||
    hasEverSubscribed(user) ||
    Boolean(options?.hasSharedWorkspace)
  );
}

/** Where a signed-in user should land after auth (login, confirm, index). */
export function getPostAuthPath(
  user: AuthUser | null | undefined,
  options?: { hasSharedWorkspace?: boolean },
): string {
  if (!user) return "/login";
  if (!canEnterApp(user, options)) return SUBSCRIBE_PATH;
  if (hasPaidAccess(user) && !isInitialOnboardingComplete(user)) {
    return ONBOARDING_PATH;
  }
  return DASHBOARD_PATH;
}

export function withOnboardingComplete(
  preferences: PreferenceBag,
): Record<string, unknown> {
  return {
    ...(preferences && typeof preferences === "object" ? preferences : {}),
    initialOnboardingCompleted: true,
  };
}
